import { useMemo } from 'react'
import {
  messageFromError,
  type ReportColumnDisplay,
  type ReportColumnValue,
  type ReportKpiInput,
  type ReportKpiSuccess,
  type ReportKpiType,
} from '@terros/common'
import { hashKey, type Query, type QueryKey, useQueries } from '@tanstack/react-query'
import { type TinyQueryOptions } from '../query'
import { reportKpiKey } from '../keys'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { type ReportStatGroupBy, toReportStatGroupBy } from './parser'
import { useReportKpiV2 } from './kpi'

export type DecoratedReportKpiInput = Omit<ReportKpiInput, 'kpis'> & {
  kpis: ReportKpiInput['kpis']
}
export const useDecoratedReportKpiV2 = (
  input: DecoratedReportKpiInput,
  options?: TinyQueryOptions
): GetHookResponse<DecoratedReportKpiRow[]> & { totalRow?: DecoratedReportKpiRow } => {
  const kpiValues = useMemo(() => input.kpis.map((kpi) => (typeof kpi === 'string' ? kpi : kpi.value)), [input.kpis])
  const reportInput = useMemo(
    () => ({ ...input, kpis: input.kpis.map((kpi) => (typeof kpi === 'string' ? { value: kpi } : kpi)) }),
    [input]
  )
  const { data, ...state } = useReportKpiV2(reportInput, options)

  const { records, totalRow } = useMemo(() => {
    if (!data) return {}
    if (data.format !== 'table') throw new Error('Invalid data format, expected tabular data')
    return {
      records: data.rows.map((row) => decorateRow(row, kpiValues)),
      totalRow: data.totalRow ? decorateRow(data.totalRow, kpiValues) : undefined,
    }
  }, [data, kpiValues])

  return { ...state, data: records, totalRow }
}
export type DecoratedReportKpiQueryResult = {
  data?: DecoratedReportKpiRow[]
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  error?: string
}

export type DecoratedReportKpiQuery = {
  input: DecoratedReportKpiInput
  enabled?: boolean
}

type ReportKpiPlaceholderData = (
  previousData: ReportKpiSuccess | undefined,
  previousQuery: Query<ReportKpiSuccess, Error> | undefined
) => ReportKpiSuccess | undefined

export type DecoratedReportKpiQueryOptions = {
  queryKey: QueryKey
  queryFn: () => Promise<ReportKpiSuccess>
  retry: false
  enabled: boolean
  placeholderData: ReportKpiPlaceholderData
}

export const toDecoratedKpiQueryOptions = (
  { input, enabled = true }: DecoratedReportKpiQuery,
  serverRequest: (input: ReportKpiInput) => Promise<ReportKpiSuccess>
): DecoratedReportKpiQueryOptions => {
  const reportInput: ReportKpiInput = {
    ...input,
    kpis: input.kpis.map((kpi) => (typeof kpi === 'string' ? { value: kpi } : kpi)),
    version: 2,
  }
  return {
    queryKey: reportKpiKey(reportInput),
    queryFn: () => serverRequest(reportInput),
    retry: false,
    enabled,
    placeholderData: toReportKpiPlaceholderData(reportInput),
  }
}

const toReportKpiPlaceholderData = (input: ReportKpiInput): ReportKpiPlaceholderData => {
  return (previousData, previousQuery) => {
    if (!previousQuery) return undefined
    const previousInput = previousQuery.queryKey[1]
    if (!isReportKpiInputEntry(previousInput)) return undefined
    if (!isSameReportContext(previousInput, input)) return undefined
    return previousData
  }
}

const isReportKpiInputEntry = (value: unknown): value is ReportKpiInput => {
  if (typeof value !== 'object' || value === null) return false
  return 'kpis' in value
}

const isSameReportContext = (a: ReportKpiInput, b: ReportKpiInput): boolean =>
  hashKey(toReportContext(a)) === hashKey(toReportContext(b))

const toReportContext = (input: ReportKpiInput): QueryKey => [
  input.kpis,
  input.filter,
  input.grouping?.type,
  input.grouping && 'level' in input.grouping ? input.grouping.level : undefined,
  input.showArchived,
]

type DecoratedKpiObserverResult = {
  data: ReportKpiSuccess | undefined
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  error: Error | null
}

export const combineDecoratedKpiResults = (
  results: DecoratedKpiObserverResult[],
  queries: DecoratedReportKpiQuery[]
): DecoratedReportKpiQueryResult[] =>
  results.map((result, index) => ({
    data: toDecoratedRows(result.data, queries[index].input.kpis),
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isPlaceholderData: result.isPlaceholderData,
    error: messageFromError(result.error),
  }))

export const useDecoratedReportKpiV2Queries = (queries: DecoratedReportKpiQuery[]): DecoratedReportKpiQueryResult[] => {
  const serverRequest = useServerRequest<ReportKpiInput, ReportKpiSuccess>('/report/kpi')

  return useQueries({
    queries: queries.map((query) => toDecoratedKpiQueryOptions(query, serverRequest)),
    combine: (results) => combineDecoratedKpiResults(results, queries),
  })
}

const toDecoratedRows = (
  data: ReportKpiSuccess | undefined,
  kpis: (ReportKpiType | ReportColumnDisplay)[]
): DecoratedReportKpiRow[] | undefined => {
  if (!data) return
  if (data.format !== 'table') return []
  const kpiValues = kpis.map((kpi) => (typeof kpi === 'string' ? kpi : kpi.value))
  return data.rows.map((row) => decorateRow(row, kpiValues))
}

export type DecoratedReportKpiRow = Record<string, ReportColumnValue>
export const decorateRow = (row: ReportColumnValue[], kpis: string[]): DecoratedReportKpiRow => {
  if (row.length !== kpis.length) throw new Error('Row length does not match kpis length')
  return kpis.reduce((acc, kpi, index) => {
    acc[kpi] = row[index]
    return acc
  }, {} as DecoratedReportKpiRow)
}

export type DecoratedReportKpiRowGroupedBy = {
  groupBy: ReportStatGroupBy
  data: DecoratedReportKpiRow
}

export const useDecoratedReportKpiV2GroupedBy = (
  input: DecoratedReportKpiInput,
  options?: TinyQueryOptions
): GetHookResponse<DecoratedReportKpiRowGroupedBy[]> => {
  const { data, ...state } = useDecoratedReportKpiV2(input, options)

  const records = useMemo(() => {
    if (!data) return
    return data.map((row) => {
      const groupBy = toReportStatGroupBy(row['groupBy'], input.grouping)
      return { groupBy, data: row }
    })
  }, [data, input])

  return { ...state, data: records }
}
